import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { Series } from '@/types/api'
import { useAuthStore } from '@/store/auth'

/**
 * useSeriesPermissions - Generell hook för att avgöra vad användaren får göra med en serie
 * 
 * Denna hook implementerar samma logik som backend's SeriesStateValidator.
 * Den kontrollerar både series state OCH användarens behörigheter.
 * 
 * @example
 * ```tsx
 * const permissions = useSeriesPermissions(series)
 * 
 * <Button 
 *   disabled={!permissions.canReportMatches}
 *   onClick={handleReport}
 * >
 *   {t('series.report.match')}
 * </Button>
 * 
 * {permissions.completionBlockMessage && (
 *   <Alert variant="warning">
 *     {permissions.completionBlockMessage}
 *   </Alert>
 * )}
 * ```
 */
export function useSeriesPermissions(series: Series | null) {
  const { t } = useTranslation()
  const { isPlatformOwner, isClubAdmin } = useAuthStore()

  return useMemo(() => {
    // No series = no permissions
    if (!series) {
      return {
        // Series state checks
        isCompleted: false,
        isActive: false,
        canModify: false,

        // Specific permission checks
        canReportMatches: false,
        canEditMatches: false,
        canDeleteMatches: false,
        canCompleteSeries: false,
        canEditSeries: false,
        canDeleteSeries: false,

        // Tournament-specific permissions
        canCreateBracket: false,
        canAdvanceKnockout: false,
        canReportKnockoutMatch: false,
        canCreateGroupStage: false,
        canCompleteGroupStage: false,
        canAdvanceToKnockout: false,
        canReportGroupMatch: false,

        // User feedback
        completionBlockMessage: null,
        permissionBlockMessage: t('series.errors.series_not_loaded'),
      }
    }

    // State checks
    const isCompleted = series.state === 'SERIES_STATE_COMPLETED'
    const isActive = series.state === 'SERIES_STATE_ACTIVE'
    const canModify = !isCompleted

    // User permission checks
    const isOwner = isPlatformOwner()
    const isAdmin = series.clubId ? isClubAdmin(series.clubId) : false
    const hasAdminRights = isOwner || isAdmin

    // Combined permission logic
    // For OPEN series, allow match reporting even if state is not explicitly ACTIVE
    // (many series start in UNSPECIFIED state)
    const isOpenSeries = series.format === 'SERIES_FORMAT_OPEN_PLAY'
    const canReportMatches = canModify && (isActive || isOpenSeries)
    const canEditMatches = canModify && hasAdminRights
    const canDeleteMatches = canModify && hasAdminRights
    const canCompleteSeries = canModify && hasAdminRights
    const canEditSeries = canModify && hasAdminRights
    const canDeleteSeries = hasAdminRights // Allow delete even if completed (rare admin action)

    // Tournament-specific permissions
    const canCreateBracket = canModify && hasAdminRights
    const canAdvanceKnockout = canModify && hasAdminRights
    const canReportKnockoutMatch = canModify && isActive
    const canCreateGroupStage = canModify && hasAdminRights
    const canCompleteGroupStage = canModify && hasAdminRights
    const canAdvanceToKnockout = canModify && hasAdminRights
    const canReportGroupMatch = canModify && isActive

    // User feedback messages
    const completionBlockMessage = isCompleted
      ? t('series.errors.completed_no_modifications', 'Serien är avslutad. Du kan inte längre rapportera eller ändra matcher.')
      : null

    const permissionBlockMessage = !hasAdminRights && !isActive
      ? t('series.errors.no_admin_rights', 'Du har inte behörighet att utföra denna åtgärd.')
      : null

    return {
      // Series state checks
      isCompleted,
      isActive,
      canModify,

      // Specific permission checks
      canReportMatches,
      canEditMatches,
      canDeleteMatches,
      canCompleteSeries,
      canEditSeries,
      canDeleteSeries,

      // Tournament-specific permissions
      canCreateBracket,
      canAdvanceKnockout,
      canReportKnockoutMatch,
      canCreateGroupStage,
      canCompleteGroupStage,
      canAdvanceToKnockout,
      canReportGroupMatch,

      // User feedback
      completionBlockMessage,
      permissionBlockMessage,

      // Helper for any custom permission check
      hasAdminRights,
    }
  }, [series, isPlatformOwner, isClubAdmin, t])
}

/**
 * Type definition för permissions-objektet
 * Exporteras för att kunna användas i andra komponenter
 */
export type SeriesPermissions = ReturnType<typeof useSeriesPermissions>
